// ============================================================
// Analytics v2 — per-project configuration
//
// Everything customer-specific (custom events, dimensions, metrics,
// privacy policy, sub-tenant mode) is expressed here — never in code.
// YOM-PLATFORM-PLAN.md §3, §5, §6.
// ============================================================
import { RETENTION_POLICY } from '../constants/index.js';
import { DEFAULT_CONSENT_CONFIG, parseConsentConfig } from './consent.js';
import { parseIntentConfig } from './intent/config.js';
import { CUSTOM_DIMENSION_SLOTS, CUSTOM_METRIC_SLOTS } from './schema.js';
export const EVENT_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;
export const PARAM_KEY_RE = /^[A-Za-z][A-Za-z0-9_.]{0,63}$/;
export const SUB_TENANT_RE = /^[A-Za-z0-9_.:-]{1,64}$/;
/** Max keys in an event spec's `required` / `pii` lists. */
export const MAX_SPEC_PARAM_LIST = 50;
/**
 * What a NEW project starts from (v1 and v2 project creation), merged under any template:
 * sub-tenants are created on their first event, with zero setup (`open`). Existing projects
 * keep their stored mode; a project with no stored config (created before this default)
 * still reads DEFAULT_ANALYTICS_CONFIG below, so nothing changes for it.
 */
export const NEW_PROJECT_CONFIG_DEFAULTS = Object.freeze({
    subTenants: { mode: 'open' },
});
/** Fallback for projects without a stored config row (legacy): sub-tenants off. */
export const DEFAULT_ANALYTICS_CONFIG = {
    version: 1,
    timezone: 'UTC',
    currency: 'USD',
    // Capped per plan at read time (effectiveRetentionDays): Free keeps 180 days.
    retentionDays: RETENTION_POLICY.payg.defaultDays,
    subTenants: { mode: 'off' },
    events: { mode: 'open', custom: {} },
    dimensions: [],
    metrics: [],
    privacy: {
        consentMode: 'optional',
        dnt: 'cookieless',
        cookielessVisitorHash: false,
        scrubParams: true,
        allowedOrigins: [],
        suppressForwardingOnOptOut: false,
    },
    destinations: { headerPrefix: 'YOM', userAgent: 'YOM-Webhooks/1.0' },
    consent: DEFAULT_CONSENT_CONFIG,
};
export class ConfigValidationError extends Error {
    issues;
    constructor(issues) {
        super(`invalid analytics config: ${issues.join('; ')}`);
        this.issues = issues;
    }
}
const TZ_RE = /^[A-Za-z_]+(\/[A-Za-z0-9_+-]+){0,2}$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
const HOST_RE = /^[a-z0-9.-]{1,253}$/;
function isObj(v) {
    return !!v && typeof v === 'object' && !Array.isArray(v);
}
/**
 * Validate + normalise a (partial) config. Unknown keys are ignored; missing
 * keys take defaults. Throws ConfigValidationError listing every issue.
 */
export function parseAnalyticsConfig(raw, opts = {}) {
    const issues = [];
    const src = isObj(raw) ? raw : {};
    const d = DEFAULT_ANALYTICS_CONFIG;
    const out = structuredClone(d);
    if (src.timezone !== undefined) {
        if (typeof src.timezone === 'string' && TZ_RE.test(src.timezone)) {
            out.timezone = src.timezone;
        }
        else {
            issues.push('timezone must be an IANA name');
        }
    }
    if (src.currency !== undefined) {
        if (typeof src.currency === 'string' && CURRENCY_RE.test(src.currency)) {
            out.currency = src.currency;
        }
        else {
            issues.push('currency must be ISO 4217');
        }
    }
    if (src.retentionDays !== undefined) {
        const n = Number(src.retentionDays);
        const max = opts.maxRetentionDays ?? RETENTION_POLICY.payg.maxDays;
        if (Number.isInteger(n) && n >= 1 && n <= max) {
            out.retentionDays = n;
        }
        else {
            issues.push(`retentionDays must be an integer 1…${max}`);
        }
    }
    if (src.subTenants !== undefined) {
        const mode = isObj(src.subTenants) ? src.subTenants.mode : undefined;
        if (mode === 'off' || mode === 'open' || mode === 'registered') {
            out.subTenants.mode = mode;
        }
        else {
            issues.push('subTenants.mode must be off|open|registered');
        }
    }
    if (src.events !== undefined) {
        if (!isObj(src.events)) {
            issues.push('events must be an object');
        }
        else {
            const mode = src.events.mode;
            if (mode !== undefined) {
                if (mode === 'open' || mode === 'strict') {
                    out.events.mode = mode;
                }
                else {
                    issues.push('events.mode must be open|strict');
                }
            }
            const custom = src.events.custom;
            if (custom !== undefined) {
                if (!isObj(custom)) {
                    issues.push('events.custom must be an object');
                }
                else {
                    const names = Object.keys(custom);
                    if (names.length > 500) {
                        issues.push('events.custom: max 500 events');
                    }
                    for (const name of names) {
                        if (!EVENT_NAME_RE.test(name)) {
                            issues.push(`events.custom.${name}: invalid event name`);
                            continue;
                        }
                        const spec = isObj(custom[name]) ? custom[name] : {};
                        const clean = {};
                        for (const k of ['label', 'itemId', 'value', 'qty', 'duration']) {
                            const v = spec[k];
                            if (v === undefined) {
                                continue;
                            }
                            if (typeof v === 'string' && PARAM_KEY_RE.test(v)) {
                                clean[k] = v;
                            }
                            else {
                                issues.push(`events.custom.${name}.${k}: invalid param key`);
                            }
                        }
                        for (const k of ['required', 'pii']) {
                            const v = spec[k];
                            if (v === undefined) {
                                continue;
                            }
                            if (Array.isArray(v) &&
                                v.length <= MAX_SPEC_PARAM_LIST &&
                                v.every(x => typeof x === 'string' && PARAM_KEY_RE.test(x))) {
                                clean[k] = [...new Set(v)];
                            }
                            else {
                                issues.push(`events.custom.${name}.${k}: ≤${MAX_SPEC_PARAM_LIST} param keys`);
                            }
                        }
                        if (spec.serverOnly === true) {
                            clean.serverOnly = true;
                        }
                        if (spec.exact === true) {
                            clean.exact = true;
                            const since = Number(spec.exactSince);
                            if (Number.isInteger(since) && since > 0) {
                                clean.exactSince = since;
                            }
                        }
                        out.events.custom[name] = clean;
                    }
                }
            }
        }
    }
    if (src.dimensions !== undefined) {
        out.dimensions = [];
        if (!Array.isArray(src.dimensions)) {
            issues.push('dimensions must be an array');
        }
        else {
            const slots = new Set();
            const names = new Set();
            src.dimensions.forEach((item, i) => {
                const def = isObj(item) ? item : {};
                const slot = Number(def.slot);
                const name = def.name;
                const key = def.key;
                if (!Number.isInteger(slot) || slot < 1 || slot > CUSTOM_DIMENSION_SLOTS) {
                    issues.push(`dimensions[${i}].slot must be 1…${CUSTOM_DIMENSION_SLOTS}`);
                    return;
                }
                if (typeof name !== 'string' || !EVENT_NAME_RE.test(name)) {
                    issues.push(`dimensions[${i}].name invalid`);
                    return;
                }
                if (typeof key !== 'string' || !PARAM_KEY_RE.test(key)) {
                    issues.push(`dimensions[${i}].key invalid`);
                    return;
                }
                if (slots.has(slot) || names.has(name)) {
                    issues.push(`dimensions[${i}]: duplicate slot or name`);
                    return;
                }
                slots.add(slot);
                names.add(name);
                out.dimensions.push({ slot, name, key });
            });
        }
    }
    if (src.metrics !== undefined) {
        out.metrics = [];
        if (!Array.isArray(src.metrics)) {
            issues.push('metrics must be an array');
        }
        else {
            const slots = new Set();
            const names = new Set();
            src.metrics.forEach((item, i) => {
                const def = isObj(item) ? item : {};
                const slot = Number(def.slot);
                if (!Number.isInteger(slot) || slot < 1 || slot > CUSTOM_METRIC_SLOTS) {
                    issues.push(`metrics[${i}].slot must be 1…${CUSTOM_METRIC_SLOTS}`);
                    return;
                }
                if (typeof def.name !== 'string' || !EVENT_NAME_RE.test(def.name)) {
                    issues.push(`metrics[${i}].name invalid`);
                    return;
                }
                if (typeof def.param !== 'string' || !PARAM_KEY_RE.test(def.param)) {
                    issues.push(`metrics[${i}].param invalid`);
                    return;
                }
                if (slots.has(slot) || names.has(def.name)) {
                    issues.push(`metrics[${i}]: duplicate slot or name`);
                    return;
                }
                let events;
                if (def.events !== undefined) {
                    if (!Array.isArray(def.events) ||
                        !def.events.every(e => typeof e === 'string' && EVENT_NAME_RE.test(e))) {
                        issues.push(`metrics[${i}].events invalid`);
                        return;
                    }
                    events = def.events;
                }
                slots.add(slot);
                names.add(def.name);
                out.metrics.push({
                    slot,
                    name: def.name,
                    param: def.param,
                    ...(events ? { events } : {}),
                });
            });
        }
    }
    if (src.privacy !== undefined) {
        const p = isObj(src.privacy) ? src.privacy : {};
        if (p.consentMode !== undefined) {
            if (p.consentMode === 'optional' ||
                p.consentMode === 'required' ||
                p.consentMode === 'strict') {
                out.privacy.consentMode = p.consentMode;
            }
            else {
                issues.push('privacy.consentMode must be optional|required|strict');
            }
        }
        if (p.dnt !== undefined) {
            if (p.dnt === 'cookieless' || p.dnt === 'drop' || p.dnt === 'ignore') {
                out.privacy.dnt = p.dnt;
            }
            else {
                issues.push('privacy.dnt must be cookieless|drop|ignore');
            }
        }
        for (const k of [
            'cookielessVisitorHash',
            'scrubParams',
            'suppressForwardingOnOptOut',
        ]) {
            if (p[k] !== undefined) {
                if (typeof p[k] === 'boolean') {
                    out.privacy[k] = p[k];
                }
                else {
                    issues.push(`privacy.${k} must be boolean`);
                }
            }
        }
        if (p.allowedOrigins !== undefined) {
            if (Array.isArray(p.allowedOrigins) &&
                p.allowedOrigins.length <= 50 &&
                p.allowedOrigins.every(h => typeof h === 'string' && HOST_RE.test(h))) {
                out.privacy.allowedOrigins = p.allowedOrigins;
            }
            else {
                issues.push('privacy.allowedOrigins must be ≤50 lower-case hosts');
            }
        }
    }
    if (src.destinations !== undefined) {
        const dst = isObj(src.destinations) ? src.destinations : {};
        if (dst.headerPrefix !== undefined) {
            if (typeof dst.headerPrefix === 'string' &&
                /^[A-Z][A-Za-z0-9]{1,20}$/.test(dst.headerPrefix)) {
                out.destinations.headerPrefix = dst.headerPrefix;
            }
            else {
                issues.push('destinations.headerPrefix must be 2–21 letters/digits starting with a capital');
            }
        }
        if (dst.userAgent !== undefined) {
            if (typeof dst.userAgent === 'string' &&
                /^[A-Za-z0-9 ._/()-]{1,60}$/.test(dst.userAgent)) {
                out.destinations.userAgent = dst.userAgent;
            }
            else {
                issues.push('destinations.userAgent invalid');
            }
        }
    }
    out.consent = parseConsentConfig(src.consent, issues);
    if (src.intent !== undefined) {
        out.intent = parseIntentConfig(src.intent, issues);
    }
    if (issues.length > 0) {
        throw new ConfigValidationError(issues);
    }
    return out;
}
/** Find the dimension slot (1-based) for a public dimension name. */
export function dimensionSlot(config, name) {
    return config.dimensions.find(d => d.name === name)?.slot ?? null;
}
/** Find the metric slot (1-based) for a public metric name. */
export function metricSlot(config, name) {
    return config.metrics.find(m => m.name === name)?.slot ?? null;
}
//# sourceMappingURL=config.js.map